/**
 * `Promise.withResolvers` polyfill.
 *
 * WHY: pdf.js (pdfjs-dist 5.x, behind the shared `<PdfReader>`) calls
 * `Promise.withResolvers()`. That method is only baseline from Chrome 119 /
 * Safari 17.4 / Firefox 121 — anything older throws
 * `Promise.withResolvers is not a function`, react-pdf's render blows up, and
 * the reseller gets a crash where an invoice, quote or documentation PDF
 * should be.
 *
 * This portal is web-only, so unlike the command center it has no Tauri
 * webview to worry about — but it is also the surface with the LEAST control
 * over its browsers. It is a partner portal: whatever a reseller's IT happens
 * to have deployed is what loads it, and "your invoice is blank on that
 * machine" is not a defect anyone can debug from here. Cypress' bundled
 * Electron 118 is below the bar too, which is why the PDF e2e could not paint
 * a canvas until this landed.
 *
 * Ported from xop-edge-staff's `src/lib/promiseWithResolvers.ts`, which hit
 * the same wall when the same reader was introduced there.
 *
 * Idempotent and a no-op on any engine that already has it. Must run before
 * anything imports pdf.js — call it from the app entry (`main.tsx`).
 */
export function installPromiseWithResolvers(): void {
  const P = Promise as unknown as {
    withResolvers?: <T>() => {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  };
  if (typeof P.withResolvers === "function") return;

  P.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}
